import { Button, HStack, Icon, Stack, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FiEdit3 } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import {
  BidV2Status,
  NegotiationRoundStatus,
  useCreatePortalInitialBidMutation,
  useCreatePortalRoundBidMutation,
  useGetPortalMyBidsQuery,
  useWithdrawPortalPrivateBidMutation,
  type PortalNegotiationRoundDto,
  type PortalSourcingEventDetailDto,
} from "../../../../autogen/bff-api";
import { useApiError } from "../../../../common/errors/useApiError";
import { useConfirmationModal } from "../../../../common/useConfirmationModal";
import { urls } from "../../../../urls";

type Variant = "compact" | "full";

interface Props {
  event: PortalSourcingEventDetailDto;
  round?: PortalNegotiationRoundDto;
  variant: Variant;
}

export const PrivateBidCta = ({ event, round, variant }: Props) => {
  if (!round) return <InitialPrivateBidCta event={event} variant={variant} />;
  if (round.status !== NegotiationRoundStatus.Published) return null;
  if (!round.isCallerInvited) return null;
  return <RoundPrivateBidCta event={event} round={round} variant={variant} />;
};

const RoundPrivateBidCta = ({
  event,
  round,
  variant,
}: {
  event: PortalSourcingEventDetailDto;
  round: PortalNegotiationRoundDto;
  variant: Variant;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { trigger } = useApiError();
  const [createRoundBid, { isLoading }] = useCreatePortalRoundBidMutation();

  const handleStart = async () => {
    try {
      const result = await createRoundBid({ eventId: event.id, roundId: round.id }).unwrap();
      navigate(urls.portal.privateBid.edit.go(event.id, result.bidId));
    } catch (e) {
      trigger(e);
    }
  };

  return (
    <CtaLayout
      variant={variant}
      title={t("Submit a bid for this round")}
      subtitle={t("Create a draft and fill in price, documents and cover letter before submitting.")}
      button={t("Start round bid")}
      onStart={handleStart}
      isLoading={isLoading}
    />
  );
};

const InitialPrivateBidCta = ({ event, variant }: { event: PortalSourcingEventDetailDto; variant: Variant }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { trigger } = useApiError();
  const confirmationModal = useConfirmationModal();
  const [createInitialBid, { isLoading }] = useCreatePortalInitialBidMutation();
  const [withdraw, { isLoading: isWithdrawing }] = useWithdrawPortalPrivateBidMutation();
  const { data: myBids, isLoading: isLoadingMyBids } = useGetPortalMyBidsQuery({});
  const existingBid = myBids?.bids.find(
    (bid) => bid.eventId === event.id && (bid.status === BidV2Status.Draft || bid.status === BidV2Status.Submitted),
  );
  const isSubmitted = existingBid?.status === BidV2Status.Submitted;

  if (isLoadingMyBids) return null;

  const handleStart = async () => {
    try {
      if (existingBid) {
        navigate(urls.portal.privateBid.edit.go(event.id, existingBid.bidId));
      } else {
        const result = await createInitialBid({ eventId: event.id }).unwrap();
        navigate(urls.portal.privateBid.edit.go(event.id, result.bidId));
      }
    } catch (e) {
      trigger(e);
    }
  };

  const handleEdit = () => {
    if (!existingBid) return;
    confirmationModal.show({
      title: t("Edit bid?"),
      description: t(
        "Your submitted bid is withdrawn while you edit, and no longer counts as delivered until you submit it again.",
      ),
      confirmButtonText: t("Edit bid") ?? "Edit bid",
      onConfirmed: async () => {
        try {
          await withdraw({ eventId: event.id, bidId: existingBid.bidId }).unwrap();
          navigate(urls.portal.privateBid.edit.go(event.id, existingBid.bidId));
        } catch (e) {
          trigger(e);
        }
      },
    });
  };

  return (
    <>
      {confirmationModal.modal}
      <CtaLayout
        variant={variant}
        title={t("Submit a bid for this procurement")}
        subtitle={
          isSubmitted
            ? t("Your bid has been submitted. You can review it until the deadline.")
            : t("Create a draft and fill in price, documents and cover letter before submitting.")
        }
        button={isSubmitted ? t("View bid") : existingBid ? t("Continue bid") : t("Start bid")}
        onStart={handleStart}
        isLoading={isLoading}
        secondaryButton={isSubmitted ? t("Edit bid") ?? "Edit bid" : undefined}
        onSecondary={handleEdit}
        isSecondaryLoading={isWithdrawing}
      />
    </>
  );
};

const CtaLayout = ({
  variant,
  title,
  subtitle,
  button,
  onStart,
  isLoading,
  secondaryButton,
  onSecondary,
  isSecondaryLoading,
}: {
  variant: Variant;
  title: string;
  subtitle: string;
  button: string;
  onStart: () => void;
  isLoading: boolean;
  secondaryButton?: string;
  onSecondary?: () => void;
  isSecondaryLoading?: boolean;
}) => {
  if (variant === "compact") {
    return (
      <Button
        colorScheme="teal"
        size="sm"
        leftIcon={<Icon as={FiEdit3} />}
        onClick={onStart}
        isLoading={isLoading}
        flexShrink={0}
      >
        {button}
      </Button>
    );
  }

  return (
    <HStack
      spacing="4"
      align="center"
      justify="space-between"
      flexWrap="wrap"
      rowGap="3"
      borderWidth="1px"
      borderColor="smPrimary"
      bg="smPrimarySofter"
      rounded="lg"
      px="4"
      py="3"
    >
      <Stack spacing="0">
        <Text fontWeight="semibold" fontSize="sm">
          {title}
        </Text>
        <Text fontSize="sm" color="smMuted">
          {subtitle}
        </Text>
      </Stack>
      <HStack spacing="3" flexShrink={0}>
        {secondaryButton && onSecondary && (
          <Button
            variant="outline"
            colorScheme="teal"
            size="sm"
            bg="smBackground"
            leftIcon={<Icon as={FiEdit3} />}
            onClick={onSecondary}
            isLoading={isSecondaryLoading}
          >
            {secondaryButton}
          </Button>
        )}
        <Button colorScheme="teal" size="sm" onClick={onStart} isLoading={isLoading}>
          {button}
        </Button>
      </HStack>
    </HStack>
  );
};
